import { type TFunction } from 'i18next';
import { Duration } from 'luxon';
import { max, mean, min, sum } from 'mathjs';

import { type TimerField } from '@/types/schema/fields';
import {
  type KnackField,
  type KnackFieldKey,
  type KnackFieldType
} from '@/types/schema/KnackField';
import { type ViewTotalSummary } from '@/types/schema/LiveAppView';
import { type ViewRecord } from '@/hooks/api/queries/useViewRecordQuery';
import { type ColumnSummariesValues } from './types';

const fieldTypesAllowedForSummary: KnackFieldType[] = [
  'number',
  'currency',
  'min',
  'max',
  'sum',
  'average',
  'count',
  'rating',
  'equation',
  'timer'
];
export function formatTimerDurationToNumber(
  timerUnixTimestamp: number | null,
  fieldFormat: TimerField['format']
): number {
  // Handle null or undefined input
  if (!timerUnixTimestamp) {
    return 0;
  }

  const timestamp = Number(timerUnixTimestamp);

  const duration = Duration.fromMillis(timestamp);

  switch (fieldFormat.total_format) {
    case 'from-to':
      return duration.as('hours');

    case 'hours':
      return duration.as('hours');

    case 'seconds':
      return duration.as('seconds');

    case 'minutes':
      return duration.as('minutes');

    case 'days':
      return Number(duration.as('days').toFixed(2));

    case 'weeks':
      return Number(duration.as('weeks').toFixed(2));

    default: {
      const days = Math.floor(duration.as('days'));
      const hours = Math.floor(duration.as('hours') % 24);
      const minutes = Math.floor(duration.as('minutes') % 60);

      if (hours === 0) {
        return minutes;
      }
      if (hours < 24) {
        return hours;
      }
      return days;
    }
  }
}

export function getTimerSummarySuffix(fieldFormat: TimerField['format']): string {
  switch (fieldFormat.total_format) {
    case 'days':
      return 'days';
    case 'hours':
      return 'hours';
    case 'seconds':
      return 'seconds';
    case 'minutes':
      return 'minutes';
    case 'weeks':
      return 'weeks';
    default:
      return '';
  }
}

export function calculateSummaries(records: ViewRecord[], sourceTableFields: KnackField[]) {
  const numericColumnValues: { [key: string]: number[] } = {};

  records.forEach((record) => {
    Object.entries(record).forEach(([key, value]) => {
      const sourceTableField = sourceTableFields.find((field) => field.key === key);

      if (!sourceTableField) return;

      if (fieldTypesAllowedForSummary.includes(sourceTableField.type)) {
        if (!numericColumnValues[key]) {
          numericColumnValues[key] = [];
        }

        if (
          sourceTableField.type === 'equation' &&
          sourceTableField.format.equation_type === 'date'
        ) {
          // If the value is a date, we don't return anything
          return;
        }

        if (sourceTableField.type === 'timer' && value) {
          const timerDuration = value.to.unix_timestamp - value.from.unix_timestamp;

          numericColumnValues[key].push(
            formatTimerDurationToNumber(timerDuration, sourceTableField.format)
          );

          return;
        }

        if (sourceTableField.type === 'currency') {
          const formattedValue = value.replace(/[^0-9.-]+/g, '');
          if (formattedValue === '') return;

          numericColumnValues[key].push(Number(formattedValue));
          return;
        }

        // If the value is empty, we don't want to count it
        if (value === '') return;

        numericColumnValues[key].push(value);
      }
    });
  });

  return Object.entries(numericColumnValues).reduce((result, [key, values]) => {
    if (values.length === 0) return result;

    return {
      ...result,
      [key]: {
        sum: sum(values),
        average: mean(values),
        min: min(values),
        max: max(values)
      }
    };
  }, {} as ColumnSummariesValues);
}

export const getSummaryValue = (
  summariesValues: ColumnSummariesValues,
  fieldKey: KnackFieldKey,
  summaryType: ViewTotalSummary['calc']
) => {
  if (!summariesValues || !summariesValues[fieldKey]) return '';

  switch (summaryType) {
    case 'sum':
      return summariesValues[fieldKey].sum;
    case 'average':
      return summariesValues[fieldKey].average;
    case 'min':
      return summariesValues[fieldKey].min;
    case 'max':
      return summariesValues[fieldKey].max;
    default:
      return '';
  }
};

export const getSummaryFieldFormat = (field: KnackField) => {
  if (field.type === 'currency')
    return {
      ...field.format,
      mark_decimal: 'period',
      mark_thousands: 'comma',
      precision: 2
    };

  if (field.type === 'timer') {
    return {
      mark_decimal: 'period',
      mark_thousands: 'none',
      precision: 2
    };
  }

  return field.format;
};

export function getFormattedTimerSummariesValue(hours: number, t: TFunction) {
  const duration = Duration.fromObject({ hours }).shiftTo('days', 'hours', 'minutes').normalize();

  if (duration.days > 0 && duration.hours > 0) {
    return t('components.views.common.summaries.from_to_duration', {
      days: duration.days,
      hours: duration.hours
    });
  }
  if (duration.days > 0) {
    return t('components.views.common.summaries.days_duration', { days: duration.days });
  }
  if (duration.hours > 0) {
    return t('components.views.common.summaries.hours_duration', { hours: duration.hours });
  }
  return t('components.views.common.summaries.minutes_duration', { minutes: duration.minutes });
}

// Replace &nbsp; with a space - Used for formatting numbers e.g 100&nbsp;€ -> 100 €
export const replaceNbsp = (value: string) => value.toString().replace(/&nbsp;/g, ' ');
